import React, { useState, useMemo } from 'react';
import './CommissionManagement.css';

const MOCK_COMMISSIONS = [
  { id: 1, broker: 'CRC Group', product: 'Cyber E&O', ratePct: 12.5, earned: 5820, accrued: 1200, paid: 4620, balance: 2400 },
  { id: 2, broker: 'Amwins', product: 'Cyber Liability', ratePct: 10, earned: 3120, accrued: 800, paid: 2320, balance: 1600 },
  { id: 3, broker: 'RT Specialty', product: 'Tech E&O', ratePct: 15, earned: 10125, accrued: 2500, paid: 7625, balance: 5000 },
  { id: 4, broker: 'USI Insurance', product: 'Cyber & Privacy', ratePct: 12, earned: 15360, accrued: 3200, paid: 12160, balance: 6400 },
  { id: 5, broker: 'Burns & Wilcox', product: 'Cyber Liability', ratePct: 11, earned: 9790, accrued: 2100, paid: 7690, balance: 4200 },
  { id: 6, broker: 'CRC Group', product: 'Cyber Liability', ratePct: 12.5, earned: 3900, accrued: 0, paid: 3900, balance: 0 },
  { id: 7, broker: 'Amwins', product: 'Cyber E&O', ratePct: 10, earned: 3060, accrued: 600, paid: 2460, balance: 1200 },
  { id: 8, broker: 'RT Specialty', product: 'Cyber & Privacy', ratePct: 14, earned: 7280, accrued: 1800, paid: 5480, balance: 3600 },
];

const MOCK_PAYMENTS = [
  { id: 1, broker: 'CRC Group', amount: 4620, date: '2026-02-15', batch: 'PAY-2026-012' },
  { id: 2, broker: 'Amwins', amount: 2320, date: '2026-02-14', batch: 'PAY-2026-011' },
  { id: 3, broker: 'RT Specialty', amount: 7625, date: '2026-02-12', batch: 'PAY-2026-010' },
  { id: 4, broker: 'USI Insurance', amount: 12160, date: '2026-02-10', batch: 'PAY-2026-009' },
  { id: 5, broker: 'Burns & Wilcox', amount: 7690, date: '2026-02-08', batch: 'PAY-2026-008' },
];

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function CommissionManagement() {
  const [search, setSearch] = useState('');

  const filteredCommissions = useMemo(() => {
    if (!search.trim()) return MOCK_COMMISSIONS;
    const q = search.toLowerCase();
    return MOCK_COMMISSIONS.filter(
      (c) =>
        (c.broker || '').toLowerCase().includes(q) ||
        (c.product || '').toLowerCase().includes(q)
    );
  }, [search]);

  const stats = useMemo(() => {
    const total = MOCK_COMMISSIONS.reduce((sum, c) => sum + (c.earned || 0) + (c.accrued || 0), 0);
    const earnedYTD = MOCK_COMMISSIONS.reduce((sum, c) => sum + (c.earned || 0), 0);
    const pending = MOCK_COMMISSIONS.reduce((sum, c) => sum + (c.balance || 0), 0);
    const avgRate =
      MOCK_COMMISSIONS.length > 0
        ? MOCK_COMMISSIONS.reduce((sum, c) => sum + (c.ratePct || 0), 0) / MOCK_COMMISSIONS.length
        : 0;
    return { total, earnedYTD, pending, avgRate };
  }, []);

  return (
    <div className="com">
      <h1 className="com__title">Commission Management</h1>

      {/* Stat cards */}
      <div className="com__stats">
        <div className="com__stat-card card">
          <span className="com__stat-label">Total Commissions</span>
          <span className="com__stat-value">{formatCurrency(stats.total)}</span>
        </div>
        <div className="com__stat-card card">
          <span className="com__stat-label">Earned YTD</span>
          <span className="com__stat-value com__stat-value--green">{formatCurrency(stats.earnedYTD)}</span>
        </div>
        <div className="com__stat-card card">
          <span className="com__stat-label">Pending Payment</span>
          <span className="com__stat-value com__stat-value--yellow">{formatCurrency(stats.pending)}</span>
        </div>
        <div className="com__stat-card card">
          <span className="com__stat-label">Avg Rate</span>
          <span className="com__stat-value">{stats.avgRate.toFixed(1)}%</span>
        </div>
      </div>

      {/* Search */}
      <div className="com__search-wrap">
        <input
          className="com__search"
          type="text"
          placeholder="Search by broker or product..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className="com__search-clear" onClick={() => setSearch('')}>
            &times;
          </button>
        )}
      </div>

      {/* Commission table */}
      <div className="com__section">
        <h2 className="com__section-title">Commission Schedules</h2>
        <div className="com__table-wrap card">
          <table className="com__table">
            <thead>
              <tr>
                <th>Broker</th>
                <th>Product</th>
                <th>Rate %</th>
                <th>Earned</th>
                <th>Accrued</th>
                <th>Paid</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {filteredCommissions.length > 0 ? (
                filteredCommissions.map((c) => (
                  <tr key={c.id} className="com__table-row">
                    <td className="com__table-broker">{c.broker}</td>
                    <td>{c.product}</td>
                    <td className="com__table-rate">{c.ratePct}%</td>
                    <td className="com__table-currency">{formatCurrency(c.earned)}</td>
                    <td className="com__table-currency">{formatCurrency(c.accrued)}</td>
                    <td className="com__table-currency">{formatCurrency(c.paid)}</td>
                    <td className="com__table-currency com__table-balance">{formatCurrency(c.balance)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="com__table-empty">
                    No commission schedules match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment history */}
      <div className="com__section">
        <h2 className="com__section-title">Recent Payments</h2>
        <div className="com__table-wrap card">
          <table className="com__table">
            <thead>
              <tr>
                <th>Broker</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Batch #</th>
              </tr>
            </thead>
            <tbody>
              {MOCK_PAYMENTS.map((p) => (
                <tr key={p.id} className="com__table-row">
                  <td className="com__table-broker">{p.broker}</td>
                  <td className="com__table-currency">{formatCurrency(p.amount)}</td>
                  <td className="com__table-date">{formatDate(p.date)}</td>
                  <td className="com__table-batch">{p.batch}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
